"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseCursorPaginationQuery = exports.parseCreatedAtIdCursor = exports.CURSOR_TOKEN_CHARSET = exports.InvalidPaginationQueryError = exports.parseRequestHeaders = exports.decodeCursor = exports.encodeCursor = exports.InvalidCursorError = exports.setResponseHeaders = exports.setPagination = exports.getPagination = exports.identifier = exports.Headers = void 0;
/** Pagination request headers. */
var Headers;
(function (Headers) {
    Headers["PerPage"] = "x-pagination-per-page";
    Headers["Page"] = "x-pagination-page";
    Headers["Total"] = "x-pagination-total";
    Headers["TotalPages"] = "x-pagination-total-pages";
    Headers["HasNext"] = "x-pagination-has-next";
    /** Opaque keyset cursor for the next page (cursor-based pagination). */
    Headers["Cursor"] = "x-pagination-cursor";
})(Headers || (exports.Headers = Headers = {}));
/** Pagination identifier. */
exports.identifier = '__DEQUE_PAGINATION__';
/** Get pagination metadata from the given `data`. */
const getPagination = (data) => {
    const pagination = data[exports.identifier];
    return pagination || null;
};
exports.getPagination = getPagination;
/** Set `pagination` metadata on the given `data`. */
const setPagination = (data, pagination) => {
    const total_pages = Math.ceil(pagination.total / pagination.per_page);
    const has_next = pagination.page < total_pages;
    Object.defineProperty(data, exports.identifier, {
        value: Object.assign(Object.assign({}, pagination), { has_next,
            total_pages }),
        enumerable: false,
        configurable: false,
        writable: false
    });
    return data;
};
exports.setPagination = setPagination;
/** Set `pagination` headers on the given `res`. */
const setResponseHeaders = (res, pagination) => {
    const totalPages = isFinite(pagination.per_page)
        ? Math.ceil(pagination.total / pagination.per_page)
        : 1;
    const hasNext = pagination.page < totalPages ? 'true' : 'false';
    res.setHeader(Headers.PerPage, pagination.per_page);
    res.setHeader(Headers.Page, pagination.page);
    res.setHeader(Headers.HasNext, hasNext);
    res.setHeader(Headers.Total, pagination.total);
    res.setHeader(Headers.TotalPages, totalPages);
};
exports.setResponseHeaders = setResponseHeaders;
/** Thrown by `decodeCursor` when a cursor token is malformed. */
class InvalidCursorError extends Error {
    constructor(message = 'Invalid pagination cursor') {
        super(message);
        this.name = 'InvalidCursorError';
    }
}
exports.InvalidCursorError = InvalidCursorError;
/**
 * Cursor token format version, carried as a `<version>.` prefix. Bump this when
 * the encoding changes so a future `decodeCursor` can recognise and migrate
 * tokens minted by an older release instead of silently mis-decoding them.
 */
const CURSOR_VERSION = '1';
const RESERVED_CURSOR_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
/** Encode UTF-8 text as unpadded base64url, working in both Node and browsers. */
const toBase64Url = (text) => {
    const bytes = new TextEncoder().encode(text);
    let binary = '';
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
};
/** Decode unpadded base64url back to UTF-8 text (inverse of `toBase64Url`). */
const fromBase64Url = (value) => {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
};
/**
 * Encode a keyset cursor payload as an opaque, version-prefixed cursor token.
 * The token is URL-safe (base64url) so it can travel in a header or query string.
 */
const encodeCursor = (payload) => `${CURSOR_VERSION}.${toBase64Url(JSON.stringify(payload))}`;
exports.encodeCursor = encodeCursor;
/**
 * Decode an opaque cursor token back into its payload. Throws
 * `InvalidCursorError` when the token's version prefix is missing/unknown, when
 * the body is not a base64url-encoded JSON object of string values, or when it
 * contains a reserved key from `RESERVED_CURSOR_KEYS` — callers
 * should surface that as a 400.
 *
 * The token is attacker-controlled. Reserved keys are rejected because they
 * would otherwise survive validation as ordinary own-enumerable keys and become
 * a prototype-pollution hazard for a consumer that merges the payload. The
 * returned keys and values are NOT sanitized for any sink: consumers must bind
 * values as query parameters (never interpolate them) and validate that keys
 * match the columns they paginate on.
 *
 * The thrown message names the failing condition (for server-side logs) but
 * never echoes the token; surface a generic 400 to clients rather than
 * forwarding it.
 */
const decodeCursor = (token) => {
    const separator = token.indexOf('.');
    const version = separator === -1 ? '' : token.slice(0, separator);
    if (version !== CURSOR_VERSION) {
        throw new InvalidCursorError('Unsupported pagination cursor version');
    }
    let parsed;
    try {
        parsed = JSON.parse(fromBase64Url(token.slice(separator + 1)));
    }
    catch (_a) {
        throw new InvalidCursorError('Pagination cursor is not valid base64url-encoded JSON');
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new InvalidCursorError('Pagination cursor must be a JSON object');
    }
    for (const [key, value] of Object.entries(parsed)) {
        if (RESERVED_CURSOR_KEYS.has(key)) {
            throw new InvalidCursorError('Pagination cursor contains a reserved key');
        }
        if (typeof value !== 'string') {
            throw new InvalidCursorError('Pagination cursor values must be strings');
        }
    }
    return parsed;
};
exports.decodeCursor = decodeCursor;
/** Parse pagination headers from the given `req`. */
const parseRequestHeaders = (req) => {
    const perPage = parseInt(req.headers[Headers.PerPage], 10);
    const page = parseInt(req.headers[Headers.Page], 10);
    if (Number.isNaN(perPage) || Number.isNaN(page)) {
        return {
            offset: 0,
            limit: Infinity,
            page: 0
        };
    }
    return {
        offset: (page - 1) * perPage,
        limit: perPage,
        page
    };
};
exports.parseRequestHeaders = parseRequestHeaders;
/** Thrown when a caller's pagination query parameters are unusable. */
class InvalidPaginationQueryError extends Error {
    constructor(message) {
        super(message);
        this.name = 'InvalidPaginationQueryError';
    }
}
exports.InvalidPaginationQueryError = InvalidPaginationQueryError;
/**
 * Every character a cursor token can legitimately contain: a base64url payload
 * behind a numeric version, joined by `.`.
 *
 * Checked before `decodeCursor`, which defers to `atob` and STRIPS ASCII CR/LF
 * — so a CRLF spliced into the payload decodes cleanly and would be forwarded
 * to an upstream request header, where Node rejects it (`ERR_INVALID_CHAR`)
 * with no response attached and the caller sees a 500 instead of a 400.
 */
exports.CURSOR_TOKEN_CHARSET = /^[A-Za-z0-9._=-]+$/;
/**
 * Strict ISO-8601 with a required timezone. `Date.parse` alone is too lenient —
 * it takes "Jan 1 2026" and timezone-less forms, which Postgres `::timestamptz`
 * would then read in the server's timezone.
 */
const ISO_8601_WITH_TIMEZONE = /^\d{4}-\d{2}-\d{2}[Tt]\d{2}:\d{2}:\d{2}(\.\d+)?([Zz]|[+-]\d{2}:?\d{2})$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
/**
 * Decode a cursor token and assert it carries a usable `(created_at, id)`
 * keyset. Throws `InvalidCursorError` for anything a caller can get wrong.
 *
 * `decodeCursor` alone is not enough: it types the payload as
 * `Record<string, string>` and checks no field, so `{ foo: 'bar' }` unwraps
 * cleanly and the upstream service rejects it — a 400 the caller would see as a
 * 500. The fields are validated here so the token fails fast, locally.
 */
const parseCreatedAtIdCursor = (token) => {
    if (!exports.CURSOR_TOKEN_CHARSET.test(token)) {
        throw new InvalidCursorError('Unsupported pagination cursor version');
    }
    const payload = (0, exports.decodeCursor)(token);
    if (typeof payload.created_at !== 'string' ||
        !ISO_8601_WITH_TIMEZONE.test(payload.created_at) ||
        isNaN(Date.parse(payload.created_at)) ||
        typeof payload.id !== 'string' ||
        !UUID.test(payload.id)) {
        throw new InvalidCursorError('Invalid pagination cursor');
    }
    return payload;
};
exports.parseCreatedAtIdCursor = parseCreatedAtIdCursor;
/**
 * Read an optional string query parameter, rejecting a repeated one (koa parses
 * `?after=a&after=b` into an array).
 *
 * An empty value is ABSENT, not a value: `?page_size=` and `?after=` arrive as
 * `''`, and treating them as present would opt the request into pagination and
 * then reject it, turning a request that returned the full list into a 400. A
 * shell template `?after=${CURSOR}` with CURSOR unset produces exactly that.
 */
const readStringParam = (value, name) => {
    if (value === undefined) {
        return undefined;
    }
    if (typeof value !== 'string') {
        throw new InvalidPaginationQueryError(`\`${name}\` must be a string`);
    }
    return value === '' ? undefined : value;
};
/**
 * Parse the `page_size`/`after` query parameters shared by every cursor
 * endpoint. Pagination is OPT-IN: a request carrying neither returns
 * `enabled: false`, and callers must then leave their unpaginated path
 * untouched — there is deliberately no implicit page size, since a silent
 * default would truncate every pinned caller's response at once.
 *
 * Throws `InvalidPaginationQueryError` for a repeated or non-positive
 * `page_size`, and `InvalidCursorError` for a malformed `after`; callers should
 * surface both as a 400.
 */
const parseCursorPaginationQuery = (query, { defaultPageSize = 30, maxPageSize = 100 } = {}) => {
    const pageSizeParam = readStringParam(query.page_size, 'page_size');
    const after = readStringParam(query.after, 'after');
    let perPage = defaultPageSize;
    if (pageSizeParam !== undefined) {
        const parsed = Number(pageSizeParam);
        if (!Number.isInteger(parsed) || parsed <= 0) {
            throw new InvalidPaginationQueryError('`page_size` must be a positive integer');
        }
        perPage = Math.min(parsed, maxPageSize);
    }
    if (after !== undefined) {
        (0, exports.parseCreatedAtIdCursor)(after);
    }
    return {
        enabled: pageSizeParam !== undefined || after !== undefined,
        perPage,
        after
    };
};
exports.parseCursorPaginationQuery = parseCursorPaginationQuery;
//# sourceMappingURL=utils.js.map